/* eslint-disable */
import React, { useEffect, useState, useMemo } from 'react';
import { Button, Box, Grid, Typography, Divider } from '@mui/material';
import { api } from '../requests/axios';

interface CrochetHook {
  id: number;
  us_size: string;
  mm_size: number;
}

interface CrochetHooksChartProps {
  onSaveRef?: React.MutableRefObject<(() => Promise<{ added: number[]; removed: number[] }>) | null>;
  showSaveButton?: boolean;
}

export default function CrochetHooksChart({ onSaveRef, showSaveButton = true }: CrochetHooksChartProps) {
  const [usersCrochetHooks, setUsersCrochetHooks] = useState<CrochetHook[]>([]);
  const [crochetHooksData, setCrochetHooksData] = useState<CrochetHook[]>([]);
  const [usSizes, setUsSizes] = useState<string[]>([]);
  const [mmSizes, setMmSizes] = useState<number[]>([]);
  const [selectedHookIds, setSelectedHookIds] = useState(new Set());
  const [initialHookIds, setInitialHookIds] = useState(new Set());

  const setData = async () => {
    const usersHooksResponse = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/users/crochet_hooks.json`);
    const userData = await usersHooksResponse.data;

    const allHooksResponse = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/crochet_hooks.json`);
    const allHookData = await allHooksResponse.data;

    setUsersCrochetHooks(userData.crochet_hooks || []);
    setCrochetHooksData(allHookData.crochet_hooks || []);
    setUsSizes(allHookData.us_sizes || []);
    setMmSizes(allHookData.mm_sizes || []);
  };

  useEffect(() => {
    setData();
  }, []);

  useEffect(() => {
    const ids = new Set(usersCrochetHooks.map(h => h.id));
    setSelectedHookIds(ids);
    setInitialHookIds(ids);
  }, [usersCrochetHooks]);

  // Create a lookup map for O(1) hook access: Map<us_size, hook>
  const hookLookupMap = useMemo(() => {
    const map = new Map<string, CrochetHook>();
    crochetHooksData.forEach(hook => {
      map.set(hook.us_size, hook);
    });
    return map;
  }, [crochetHooksData]);

  const handleBoxChange = (event: React.MouseEvent<HTMLDivElement>) => {
    const id = parseInt((event.target as HTMLDivElement).id, 10);
    setSelectedHookIds(prev => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  };

  const getBackground = (id: number) => {
    return selectedHookIds.has(id) ? '#5A7D6B' : '#e0e0e0';
  };

  const getSaveData = () => {
    const added = Array.from(selectedHookIds).filter(id => !initialHookIds.has(id)) as number[];
    const removed = Array.from(initialHookIds).filter(id => !selectedHookIds.has(id)) as number[];
    return { added, removed };
  };

  const handleSave = async () => {
    const { added, removed } = getSaveData();

    try {
      await api.post('/users/bulk_update_crochet_hooks.json', { added, removed });
      setInitialHookIds(new Set(selectedHookIds));
      alert('Changes saved!');
    } catch (error) {
      console.error('Save failed:', error);
      alert('Error saving changes');
    }
  };

  // Expose save function via ref
  React.useEffect(() => {
    if (onSaveRef) {
      onSaveRef.current = async () => {
        const { added, removed } = getSaveData();
        await api.post('/users/bulk_update_crochet_hooks.json', { added, removed });
        setInitialHookIds(new Set(selectedHookIds));
        return { added, removed };
      };
    }
  }, [selectedHookIds, initialHookIds, onSaveRef]);

  return (
    <>
      <Box sx={{ 
        p: { xs: 1, sm: 2 },
        overflowX: 'auto',
        width: '100%'
      }}>
        {/* Header Row */}
        <Box sx={{ minWidth: { xs: '600px', sm: 'auto' } }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Crochet Hooks
          </Typography>
          
          <Grid container alignItems="center" spacing={0.5}>
            <Grid item sx={{ width: { xs: 100, sm: 120 }, flexShrink: 0, pr: { xs: 1, sm: 2 } }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  textAlign: 'right'
                }}
              >
                US
              </Typography>
            </Grid>
            {usSizes.map((us: string, idx: number) => {
              return (
                <Grid item key={`us-${idx}`} sx={{ 
                  minWidth: { xs: 24, sm: 30 },
                  width: { xs: 24, sm: 30 },
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexShrink: 0
                }}>
                  <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{us}</Typography>
                </Grid>
              );
            })}
          </Grid>
          <Grid container alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            <Grid item sx={{ width: { xs: 100, sm: 120 }, flexShrink: 0, pr: { xs: 1, sm: 2 } }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  textAlign: 'right'
                }}
              >
                Metric
              </Typography>
            </Grid>
            {mmSizes.map((mm: number, idx: number) => (
              <Grid item key={`mm-${idx}`} sx={{ 
                minWidth: { xs: 24, sm: 30 },
                width: { xs: 24, sm: 30 },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{mm}</Typography>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 0.75 }} />

          {/* Single Row for Crochet Hooks */}
          <Grid container alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            <Grid item sx={{ width: { xs: 100, sm: 120 }, flexShrink: 0, pr: { xs: 1, sm: 2 } }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  textAlign: 'right'
                }}
              >
                Hooks
              </Typography>
            </Grid>
            {usSizes.map((us: string, idx: number) => {
              const hook = hookLookupMap.get(us);
              return (
                <Grid item key={`hook-${us}-${idx}`} sx={{ 
                  minWidth: { xs: 24, sm: 30 },
                  width: { xs: 24, sm: 30 },
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexShrink: 0
                }}>
                  {hook ? (
                    <Box
                      id={hook.id.toString()}
                      sx={{
                        height: { xs: 16, sm: 20 },
                        width: { xs: 16, sm: 20 },
                        bgcolor: getBackground(hook.id),
                        borderRadius: 0.5,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }
                      }}
                      onClick={handleBoxChange}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: { xs: 16, sm: 20 },
                        width: { xs: 16, sm: 20 }
                      }}
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
      {showSaveButton && (
        <Button 
          variant="contained" 
          onClick={handleSave} 
          sx={{ 
            m: { xs: 1, sm: 2 },
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          Save Changes
        </Button>
      )}
    </>
  );
}

